import { BehaviorSubject } from 'rxjs';
import { addError, clearError } from './Error';
import { deleteETU, getETUs, postETU, putETU } from '../Api/ETU/ETUApi';
import { User } from './User';
import { Ticket } from './Ticket';

export interface ETU {
  id: string;
  grossCommission: number;
  commissionRun?: string;
  netBilled?: number;
  receivedAsExpected?: boolean;
  billingMonth?: Date;
  ticket: Ticket;
  createdBy: User;
  createdAt: Date;
  updatedAt?: Date;
}

export interface FilterETUDto {
  commissionRun?: string;
  ticket?: string;
  createdAt?: string;
  createdBy?: string;
}

const etuSubject = new BehaviorSubject<ETU[]>([]);
const etuDrawerSubject = new BehaviorSubject<ETU[]>([]);
const etuLoadingSubject = new BehaviorSubject<boolean>(false);
const etuDrawerLoadingSubject = new BehaviorSubject<boolean>(false);

export const getETUSubject = (): BehaviorSubject<ETU[]> => etuSubject;
export const getETUDrawerSubject = (): BehaviorSubject<ETU[]> => etuDrawerSubject;
export const getETULoadingSubject = (): BehaviorSubject<boolean> => etuLoadingSubject;
export const getETUDrawerLoadingSubject = (): BehaviorSubject<boolean> => etuDrawerLoadingSubject;

export const fetchETUs = async (filters?: FilterETUDto, drawer = false) => {
  const loading = drawer ? etuDrawerLoadingSubject : etuLoadingSubject;
  const subject = drawer ? etuDrawerSubject : etuSubject;
  loading.next(true);

  const { data, error } = await getETUs(filters);

  if (data) {
    clearError('etu');
    const sortedRows = data.sort((a: ETU, b: ETU) => {
      const timeA = new Date(a.billingMonth ?? '').getTime();
      const timeB = new Date(b.billingMonth ?? '').getTime();
      return timeA - timeB;
    });
    subject.next(sortedRows);
  }
  if (error) addError('etu', error.message);

  loading.next(false);
};

export const fetchETUsDrawer = async (filters?: FilterETUDto) => {
  return fetchETUs(filters, true);
};

export const createETU = async (etuData: ETU, drawer = false) => {
  const { data, error } = await postETU(etuData);

  if (data) {
    clearError('etu');
    const subject = drawer ? etuDrawerSubject : etuSubject;
    const currentData = [...subject.getValue()];
    currentData.push(data);
    subject.next(currentData);
  }
  if (error) addError('etu', error.message);
};

export const updateETU = async (updateData: ETU, drawer = false) => {
  const { data, error } = await putETU(updateData);

  if (data) {
    clearError('etu');
    const subject = drawer ? etuDrawerSubject : etuSubject;
    const currentData = [...subject.getValue()];
    const dataIndex = currentData.findIndex((item) => item.id === data.id);
    if (dataIndex !== -1) {
      currentData[dataIndex] = data;
    } else {
      currentData.push(data);
    }
    subject.next(currentData);
  }
  if (error) addError('etu', error.message);
};

export const removeETU = async (id: string, drawer = false) => {
  const { error } = await deleteETU(id);
  clearError('etu');
  if (error) {
    addError('etu', error.message);
  } else {
    const subject = drawer ? etuDrawerSubject : etuSubject;
    const currentData = [...subject.getValue()];
    subject.next(currentData.filter((item) => item.id !== id));
  }
};
